import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import COUNTRIES from '../../data/countries';
import SearchableSelect from '../SearchableSelect';
import { CURRENCY_OPTIONS } from '../investments/investmentConstants';
import {
  LIABILITY_TYPES, LIABILITY_FIELDS_BY_TYPE,
} from './liabilityConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Add / Edit a single liability. Type-driven schema mirrors the Investments
 * pattern: pick a liability type from the dropdown → the form re-renders to
 * show only the fields relevant to that type.
 *
 * Same security posture as the Investment modal:
 *   • JWT-authenticated calls only
 *   • Field-level encryption is server-side; the client just submits values
 *   • Validation is enforced on both client and server (no silent saves)
 */
function AddLiabilityModal({ isOpen, onClose, onSuccess, liability = null }) {
  const isEditing = Boolean(liability && liability.liability_id);

  const buildInitial = () => liability ? {
    liability_type: liability.liability_type || '',
    name: liability.name || '',
    counterparty: liability.counterparty || '',
    currency: liability.currency || '',
    country: liability.country || '',
    owner_type: liability.owner_type || 'self',
    notes: liability.notes || '',
    outstanding_balance: liability.outstanding_balance ?? '',
    original_principal: liability.original_principal ?? '',
    interest_rate: liability.interest_rate ?? '',
    start_date: liability.start_date ? String(liability.start_date).slice(0, 10) : '',
    maturity_date: liability.maturity_date ? String(liability.maturity_date).slice(0, 10) : '',
    credit_limit: liability.credit_limit ?? '',
    minimum_payment: liability.minimum_payment ?? '',
    vehicle_description: liability.vehicle_description || '',
    secured_property: liability.secured_property || '',
    secured_holding: liability.secured_holding || '',
    tax_year: liability.tax_year || '',
    due_date: liability.due_date ? String(liability.due_date).slice(0, 10) : '',
  } : {
    liability_type: '', name: '', counterparty: '', currency: '', country: '',
    owner_type: 'self', notes: '',
    outstanding_balance: '', original_principal: '', interest_rate: '',
    start_date: '', maturity_date: '', credit_limit: '', minimum_payment: '',
    vehicle_description: '', secured_property: '', secured_holding: '',
    tax_year: '', due_date: '',
  };

  const [formData, setFormData] = useState(buildInitial);
  const [saving, setSaving] = useState(false);

  // Re-seed when the modal is opened with a different liability
  useEffect(() => {
    if (isOpen) setFormData(buildInitial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, liability]);

  const typeFields = useMemo(
    () => LIABILITY_FIELDS_BY_TYPE[formData.liability_type] || [],
    [formData.liability_type],
  );

  const update = (k, v) => setFormData((d) => ({ ...d, [k]: v }));

  const validate = () => {
    if (!formData.liability_type) return 'Please choose a liability type.';
    if (!formData.name?.trim()) return 'Name is required.';
    if (!formData.currency?.trim()) return 'Currency is required.';
    for (const f of typeFields) {
      if (f.required && (formData[f.key] === '' || formData[f.key] == null)) {
        return `${f.label} is required.`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }

    // Build payload: only include keys that are non-empty so we don't write
    // empty strings into the database.
    const payload = {};
    const allowed = ['liability_type','name','counterparty','currency','country','owner_type','notes',
      'outstanding_balance','original_principal','interest_rate','credit_limit','minimum_payment',
      'vehicle_description','secured_property','secured_holding','tax_year'];
    for (const k of allowed) {
      const v = formData[k];
      if (v !== '' && v != null) payload[k] = v;
    }
    // Date conversions
    for (const k of ['start_date','maturity_date','due_date']) {
      if (formData[k]) payload[k] = `${formData[k]}T00:00:00`;
    }
    // Numeric coercion
    for (const k of ['outstanding_balance','original_principal','interest_rate','credit_limit','minimum_payment']) {
      if (payload[k] !== undefined) payload[k] = Number(payload[k]);
    }
    payload.currency = (payload.currency || '').toUpperCase();

    setSaving(true);
    try {
      const url = isEditing
        ? `${BACKEND_URL}/api/liabilities/${liability.liability_id}`
        : `${BACKEND_URL}/api/liabilities`;
      const method = isEditing ? 'PUT' : 'POST';
      const resp = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `Failed (${resp.status})`);
      }
      const saved = await resp.json();
      toast.success(isEditing ? 'Liability updated' : 'Liability added');
      onSuccess?.(saved);
      onClose?.();
    } catch (e2) {
      toast.error(e2.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-liability-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif font-bold text-slate-900">
            {isEditing ? 'Edit Liability' : 'Add New Liability'}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">
            Fields marked <span className="text-red-500 font-semibold">*</span> are required
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-900"
          data-testid="liability-modal-preamble"
        >
          Track what you owe so your Statement of Net Worth reflects the full picture.
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Liability type <span className="text-red-500">*</span></Label>
              <Select value={formData.liability_type} onValueChange={(v) => update('liability_type', v)}>
                <SelectTrigger className="mt-1" data-testid="liability-type-select"><SelectValue placeholder="Choose a type" /></SelectTrigger>
                <SelectContent>
                  {LIABILITY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`liability-type-option-${t.value}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* All remaining fields stay hidden until a liability type is chosen */}
          {!formData.liability_type ? (
            <p className="text-xs text-slate-500 italic" data-testid="liability-type-hint">
              Pick a liability type above to continue.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600">Name <span className="text-red-500">*</span></Label>
                  <Input value={formData.name} onChange={(e) => update('name', e.target.value)}
                    placeholder="e.g., Primary residence mortgage" className="mt-1" data-testid="liability-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">
                    {formData.liability_type === 'tax_owed' ? 'Tax authority' : 'Lender / counterparty'}
                  </Label>
                  <Input value={formData.counterparty} onChange={(e) => update('counterparty', e.target.value)}
                    placeholder={formData.liability_type === 'tax_owed' ? 'e.g., HMRC' : 'e.g., HSBC'}
                    className="mt-1" data-testid="liability-counterparty-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Currency <span className="text-red-500">*</span></Label>
                  <div className="mt-1">
                    <SearchableSelect
                      value={formData.currency}
                      onValueChange={(v) => update('currency', v)}
                      options={CURRENCY_OPTIONS}
                      placeholder="Select currency"
                      searchPlaceholder="Search currency..."
                      emptyMessage="No currency found."
                      dataTestId="liability-currency-select"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Country</Label>
                  <Select value={formData.country} onValueChange={(v) => update('country', v)}>
                    <SelectTrigger className="mt-1" data-testid="liability-country-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {COUNTRIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Owner</Label>
                  <Select value={formData.owner_type} onValueChange={(v) => update('owner_type', v)}>
                    <SelectTrigger className="mt-1" data-testid="liability-owner-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="self">You</SelectItem>
                      <SelectItem value="related">Related party (memo-only)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Type-specific fields */}
              {typeFields.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3" data-testid="liability-type-specific-fields">
                  <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-3">Details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {typeFields.map((f) => (
                      <div key={f.key}>
                        <Label className="text-xs text-slate-600">
                          {f.label}{f.required && <span className="text-red-500"> *</span>}
                        </Label>
                        <Input
                          type={f.kind}
                          step={f.step}
                          value={formData[f.key] ?? ''}
                          onChange={(e) => update(f.key, e.target.value)}
                          placeholder={f.placeholder || ''}
                          className="mt-1"
                          data-testid={`liability-field-${f.key}`}
                        />
                        {f.hint && <p className="text-[11px] text-slate-500 mt-1">{f.hint}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs text-slate-600">Notes (optional)</Label>
                <Textarea value={formData.notes} onChange={(e) => update('notes', e.target.value)}
                  rows={2} placeholder="Anything else worth recording — visible only to you"
                  className="mt-1" data-testid="liability-notes-input" />
              </div>
            </>
          )}

          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving} data-testid="liability-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    disabled={saving || !formData.liability_type} data-testid="liability-save-btn">
              {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              {isEditing ? 'Save changes' : 'Add liability'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default AddLiabilityModal;
